import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from "framer-motion";
import { Search, Palette, Code, Rocket, CheckCircle, ArrowRight } from "lucide-react";
import SectionBadge from "../../../components/ui/SectionBadge";

const ProcessSection = () => {
  const [expandedId, setExpandedId] = useState(null);
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };
  const y = useSpring(useTransform(scrollYProgress, [0, 1], [50, -50]), springConfig);

  const processes = [
    {
      id: 1,
      icon: Search,
      title: "Discovery & Research",
      description: "We start by understanding your goals, users, and technical needs to build the perfect foundation for your project.",
      image: "/assets/images/Discovery & Research.png",
      gradient: "from-blue-500 to-cyan-400",
      features: ["Requirements Analysis", "Market Research", "Stakeholder Interviews", "Project Roadmap"],
      details: {
        overview: "We start every project with a comprehensive discovery phase to ensure we fully understand your vision, goals, and challenges.",
        deliverables: [
          "Detailed project scope document",
          "User personas and journey maps",
          "Competitive analysis report",
          "Technical feasibility assessment",
          "Timeline and milestone plan"
        ],
        duration: "1-2 weeks",
        outcome: "A clear roadmap that aligns stakeholders and sets the foundation for success."
      }
    },
    {
      id: 2,
      icon: Palette,
      title: "Design & Planning",
      description: "Our team creates intuitive, visually appealing designs and a technical blueprint that ensures scalability and long-term success.",
      image: "/assets/images/Design & Planning2.png",
      gradient: "from-purple-500 to-pink-400",
      features: ["UI/UX Design", "Wireframing", "Architecture Planning", "Design System"],
      details: {
        overview: "Our design team creates beautiful, intuitive experiences while our architects build a solid technical foundation.",
        deliverables: [
          "High-fidelity mockups and prototypes",
          "Interactive wireframes",
          "System architecture diagrams",
          "Design system and component library",
          "Database schema design"
        ],
        duration: "2-4 weeks",
        outcome: "Validated designs and a scalable technical blueprint ready for development."
      }
    },
    {
      id: 3,
      icon: Code,
      title: "Development & Testing",
      description: "We convert ideas into high-quality, production-ready solutions through clean coding practices and robust testing.",
      image: "/assets/images/Development & Testing.png",
      gradient: "from-green-500 to-emerald-400",
      features: ["Agile Development", "Code Reviews", "QA Testing", "Performance Optimization"],
      details: {
        overview: "We build your solution using modern best practices, with continuous testing and refinement throughout.",
        deliverables: [
          "Clean, well-documented codebase",
          "Automated test suites",
          "Performance optimization reports",
          "Security audit and fixes",
          "Staging environment for review"
        ],
        duration: "4-12 weeks",
        outcome: "A robust, tested application that meets all requirements and exceeds quality standards."
      }
    },
    {
      id: 4,
      icon: Rocket,
      title: "Launch & Support",
      description: "We ensure a smooth launch and provide continuous improvements so your solution stays fast, secure, and future-ready.",
      image: "/assets/images/Launch & Support.png",
      gradient: "from-orange-500 to-yellow-400",
      features: ["Deployment", "Training", "24/7 Monitoring", "Continuous Updates"],
      details: {
        overview: "We ensure a smooth launch and provide ongoing support to keep your solution running optimally.",
        deliverables: [
          "Production deployment",
          "User training materials and sessions",
          "Documentation and handover",
          "Monitoring and analytics setup",
          "Maintenance and support plan"
        ],
        duration: "1-2 weeks + ongoing",
        outcome: "A live, well-supported application with your team empowered to succeed."
      }
    }
  ];

  return (
    <section ref={sectionRef} className="relative py-20 overflow-hidden bg-gray-50 border-t border-gray-200">


      {/* Calmer orbs */}
      <motion.div
        style={{ y: useTransform(scrollYProgress, [0, 1], [0, 100]) }}
        className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-15"
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        style={{ y: useTransform(scrollYProgress, [0, 1], [0, -100]) }}
        className="absolute bottom-20 left-10 w-80 h-80 bg-gradient-to-br from-pink-400 to-orange-400 rounded-full mix-blend-multiply filter blur-3xl opacity-15"
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="container mx-auto relative z-10 3xl:max-w-[1800px]">
        {/* Section Header */}
        <motion.div
          className="text-center max-w-4xl 3xl:max-w-5xl 4xl:max-w-6xl mx-auto mb-12 md:mb-20 3xl:mb-32"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <SectionBadge icon={CheckCircle} text="Our Process" />
          <h2 className="text-4xl md:text-5xl 3xl:text-6xl font-bold mb-6 3xl:mb-8 bg-gradient-to-r from-slate-900 via-blue-800 to-purple-900 bg-clip-text text-transparent leading-normal py-1">
            How We Bring Your Vision to Life
          </h2>
          <p className="text-xl 3xl:text-2xl 4xl:text-3xl 5xl:text-4xl 6xl:text-5xl text-slate-600 leading-relaxed max-w-3xl 3xl:max-w-4xl 4xl:max-w-5xl mx-auto">
            A clear, structured process that blends strategy, design, engineering, and long-term support.
          </p>
        </motion.div>

        {/* Process Steps */}
        <div className="space-y-16 md:space-y-24 lg:space-y-32 3xl:space-y-48">
          {processes.map((process, index) => {
            const isEven = index % 2 === 0;

            return (
              <motion.div
                key={process.id}
                className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 md:gap-12 lg:gap-16 3xl:gap-24 items-center`}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6 }}
              >
                {/* Content Side */}
                <div className="flex-1 space-y-4 sm:space-y-6 3xl:space-y-8">
                  <motion.div
                    className="flex items-center gap-3 sm:gap-4 3xl:gap-6"
                    initial={{ opacity: 0, x: isEven ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                  >
                    <motion.div
                      className={`relative w-16 h-16 sm:w-20 sm:h-20 3xl:w-24 3xl:h-24 rounded-2xl bg-gradient-to-br ${process.gradient} flex items-center justify-center shadow-lg`}
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      <process.icon className="w-8 h-8 sm:w-10 sm:h-10 3xl:w-12 3xl:h-12 text-white relative z-10" />
                    </motion.div>

                    <div>
                      <div className={`text-xs sm:text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl font-bold bg-gradient-to-r ${process.gradient} bg-clip-text text-transparent uppercase tracking-wider`}>
                        Step {process.id}
                      </div>
                      <h3 className="text-2xl sm:text-3xl md:text-4xl 3xl:text-5xl 4xl:text-6xl 5xl:text-7xl 6xl:text-8xl font-black text-gray-900">
                        {process.title}
                      </h3>
                    </div>
                  </motion.div>

                  <motion.p
                    className="text-base sm:text-lg 3xl:text-xl 4xl:text-2xl 5xl:text-3xl 6xl:text-4xl text-gray-600 leading-relaxed"
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  >
                    {process.description}
                  </motion.p>

                  <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 3xl:gap-4"
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    {process.features.map((feature, i) => (
                      <motion.div
                        key={i}
                        className="flex items-center gap-2"
                        initial={{ opacity: 0, x: -15 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: 0.3 + i * 0.05 }}
                      >
                        <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 3xl:w-2.5 3xl:h-2.5 rounded-full bg-gradient-to-r ${process.gradient}`} />
                        <span className="text-xs sm:text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl font-semibold text-gray-700">
                          {feature}
                        </span>
                      </motion.div>
                    ))}
                  </motion.div>
                </div>

                {/* Image Side */}
                <motion.div
                  className="flex-1 w-full"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <motion.div
                    className="relative group"
                    whileHover={{ scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <motion.div
                      className={`absolute -inset-1 bg-gradient-to-r ${process.gradient} rounded-3xl blur-2xl opacity-30`}
                      animate={{ scale: [1, 1.03, 1] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    />

                    <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                      <img
                        src={process.image}
                        alt={process.title}
                        className="w-full h-full object-cover aspect-[4/3]"
                      />
                      <div className={`absolute inset-0 bg-gradient-to-br ${process.gradient} opacity-20`} />

                      <motion.div
                        className="absolute top-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl px-6 py-3 3xl:px-8 3xl:py-4 shadow-xl"
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 0.4 }}
                      >
                        <div className={`text-2xl 3xl:text-3xl font-black bg-gradient-to-r ${process.gradient} bg-clip-text text-transparent`}>
                          0{process.id}
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                </motion.div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="mt-16 md:mt-24 lg:mt-32 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="inline-block p-1 rounded-2xl sm:rounded-3xl bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            <a
              href="/contact-us"
              className="block bg-white px-6 sm:px-12 py-4 sm:py-6 rounded-2xl sm:rounded-3xl font-bold text-base sm:text-xl 3xl:text-2xl 4xl:text-3xl 5xl:text-4xl 6xl:text-5xl text-gray-900 hover:bg-gray-50 transition-colors"
            >
              Ready to Start Your Project?
              <div className="text-xs sm:text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl text-gray-600 mt-1 font-normal">
                Let's discuss how we can help you succeed
              </div>
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessSection;